/**
 * frontCore 의 런타임 설정 접점.
 *
 * 코어(`lib/api`·`lib/auth` 등)는 웹·앱이 공유하는데, 값의 출처가 플랫폼마다 다르다 — 웹은 Vite 의
 * `import.meta.env`, 앱(RN)은 Metro/Expo 의 환경이다. 그래서 코어는 플랫폼 전용 API 를 <b>직접 읽지 않고</b>
 * 시작 시 여기로 주입받은 값만 읽는다. 웹은 `lib/env.ts` 가, 앱은 자기 부트스트랩이 {@link configureRuntime}
 * 를 부른다.
 *
 * (배경: docs/04_2026-07-28_cross-platform-mobile-design.md — frontCore 분리 준비)
 */

export type RuntimeConfig = {
  /** 백엔드 기본 URL. 웹 same-origin 은 `/api`, 앱은 절대 URL 이 된다. */
  apiBaseUrl: string;
  /** 개발 빌드인가. 개발 편의 동작(목 세션 등)의 게이트로 쓴다. */
  isDev: boolean;
  /** 개발 중 백엔드에 닿지 못할 때 목 세션으로 화면을 열지. 운영에서는 항상 false. */
  mockSessionWhenOffline: boolean;
  /**
   * 웹인가 앱인가 — 요청마다 `X-Bomi-Client` 로 실린다(백엔드 `ClientKind`).
   *
   * 권한이 아니라 <b>그릴 수 있는가</b>를 말하는 값이다. 웹에만 있는 화면으로 답해야 하는 것은
   * 앱에 내려보내지 않는다 — 첫 자리가 홈페이지 시안 투표다(V350): 앱에는 시안을 띄울 코드가
   * 없어, 이름만 보고 고른 표가 섞이면 물어본 값이 아니게 된다.
   */
  client: 'web' | 'app';
};

/** 주입 전 기본값. 웹 same-origin 을 가정한다 — 부트스트랩이 곧 실제 값으로 덮는다. */
const DEFAULTS: RuntimeConfig = {
  apiBaseUrl: '/api',
  isDev: false,
  mockSessionWhenOffline: false,
  // 주입 전 기본은 웹이다. 못 그리는 화면을 감추는 값이라, 모르면 <b>감추지 않는</b> 쪽이 맞다.
  client: 'web',
};

let current: RuntimeConfig = DEFAULTS;

/** 플랫폼 부트스트랩이 시작 시 한 번 부른다. 준 값만 덮고 나머지는 그대로 둔다. */
export function configureRuntime(config: Partial<RuntimeConfig>): void {
  current = { ...current, ...config };
}

/** 코어가 읽는 현재 런타임 설정. */
export function runtimeConfig(): RuntimeConfig {
  return current;
}
